import { datadogRum } from "@datadog/browser-rum";
import { FeatureFlag } from "features/Statsig/constants";
import { useFeatureGate } from "features/Statsig";
import { ThreadInteraction } from "features/Thread/types";
import useUserSettings from "features/User/hooks/useUserSettings";
import { getEventStreamForThreadInteractionAnalysis } from "helpers/api";
import { getThreadInteractionAnalysisEndpointParams } from "helpers/endpointQueryParams";
import useThread from "hooks/useThread";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo } from "react";
import useStreaming from "./useStreaming";
import { useStreamingBufferedContent } from "./useStreamingBufferedContent";

type UseStreamingForThreadInteractionProps = {
  threadId: string;
  interaction: ThreadInteraction;
  enabled: boolean;
  onSuccess?: () => void;
};

const useStreamingForThreadInteractionAnalysis = ({
  threadId,
  interaction,
  onSuccess = () => {},
  enabled,
}: UseStreamingForThreadInteractionProps) => {
  const router = useRouter();
  const { updateThreadInteractionCacheById } = useThread(threadId);

  const isFullTextEnabled = useFeatureGate(
    FeatureFlag.ALLOW_FULL_TEXT_ENTITLEMENT
  ).value;

  const isProEnabled = interaction.is_pro_enabled;

  const { lang } = useUserSettings();

  const queryKey = useMemo(
    () => ["thread_interaction_analysis", threadId, interaction.id],
    [threadId, interaction.id]
  );
  const queryKeyString = JSON.stringify(queryKey);

  const getEventSource = useCallback(() => {
    const queryParams = getThreadInteractionAnalysisEndpointParams({
      ...router.query,
      lang,
      is_full_text_enabled: isFullTextEnabled,
    });
    const eventSource = getEventStreamForThreadInteractionAnalysis(
      threadId,
      interaction.id,
      queryParams
    );

    return eventSource;
  }, [threadId, interaction.id, router.query, lang, isFullTextEnabled]);

  const onError = useCallback(
    (errorMessage: string, isAuthError: boolean) => {
      // Log error to DataDog
      if (typeof window !== "undefined" && datadogRum) {
        datadogRum.addError(
          new Error(
            `StreamingError in ThreadInteractionAnalysis: ${errorMessage}`
          ),
          {
            context: {
              errorType: isAuthError
                ? "Authentication Error"
                : "Streaming Error",
              originalError: errorMessage,
              interactionId: interaction.id,
              threadId,
              searchId: interaction.search_id,
              searchMode: interaction.search_mode,
              timestamp: new Date().toISOString(),
              url: window.location.href,
              queryKey: queryKeyString,
              streamPosition: 0,
            },
            source: "custom",
          }
        );
      }
    },
    [interaction, threadId, queryKeyString]
  );

  const streamingResult = useStreaming({
    queryKey: queryKeyString,
    onSuccess,
    onError,
    enabled,
    getEventSource,
  });

  const {
    isComplete,
    isError,
    isAuthError,
    content,
    resultCount,
    isIncompleteResults,
    isNuanceRequired,
    fullTextPaperIds,
  } = streamingResult;

  const bufferedContent = useStreamingBufferedContent({
    content,
  });

  // When the isIncompleteResults flag is updated and is true, save it back to the full thread store and disable pro.
  useEffect(() => {
    if (isIncompleteResults) {
      updateThreadInteractionCacheById(interaction.id, {
        not_enough_relevant_results: isIncompleteResults,
        is_pro_enabled: false,
      });
    }
  }, [isIncompleteResults, interaction.id, updateThreadInteractionCacheById]);

  // If the analysis errors, save it back to the thread so that we can e.g. hide the analysis progress bar.
  useEffect(() => {
    if (isError) {
      updateThreadInteractionCacheById(interaction.id, {
        isAnalysisError: isError || isAuthError,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, interaction.id, updateThreadInteractionCacheById]);

  // When the isNuanceRequired flag is updated and is true, disable pro.
  useEffect(() => {
    if (isNuanceRequired === true) {
      updateThreadInteractionCacheById(interaction.id, {
        is_pro_enabled: false,
      });
    }
  }, [isNuanceRequired, interaction.id, updateThreadInteractionCacheById]);

  // When streaming completes, save the analysis and result count back to the full thread store.
  // If the user is not premium and the resulting analysis is pro, spend credits on pro analysis.
  useEffect(() => {
    if (isComplete) {
      updateThreadInteractionCacheById(interaction.id, {
        analysis: content,
        num_results_analyzed: resultCount,
        full_text_paper_ids: fullTextPaperIds,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    content,
    isComplete,
    interaction.id,
    resultCount,
    fullTextPaperIds,
    isProEnabled,
  ]);

  return {
    ...streamingResult,
    bufferedContent,
  };
};

export default useStreamingForThreadInteractionAnalysis;
